/**
 * features/finance/PastelFixCard.tsx
 * ------------------------------------------------------------------
 * FINANCE — one Pastel-vs-portal difference, and the buttons that fix it.
 *
 * The card shows both balances and the difference, then the fixes that
 * fit (lib/reconFixes), the likeliest first. Choosing one opens a short
 * form with the amount and details already filled in; confirming runs the
 * write (reconFixWrite) through the portal's ordinary finance paths. The
 * comparison is live, so a card whose difference is closed simply goes.
 *
 * From the Detailed Ledger (Oct 2026) the card also lists Pastel's own
 * entries since the pupil's opening balance — the evidence — and a fix
 * built from a Pastel receipt arrives with its date and RC number filled.
 * A portal payment Pastel has no receipt for is named in a banner — who
 * entered it and when — with "reverse it" and "keep it" beside it.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { Link2, PenLine, Ban, HandCoins, FilePlus2, StickyNote, Loader2, Check, ListTree, RotateCcw, AlertTriangle, UserSearch, UserPlus } from 'lucide-react';
import { btnPrimary, btnGhost, glassFieldClass } from '@/features/finance/ui';
import {
  fmtBalance, FEE_CATEGORY_LABELS, PAYMENT_METHOD_LABELS,
  type FeeCategory, type Invoice, type PaymentMethod,
} from '@/lib/financeCore';
import { SCHOOL, currentTermKey, termLabel, type TermKey } from '@/lib/school.config';
import type { DiffRow } from '@/lib/pastelAgeAnalysis';
import { nameLikeness, type Fix, type FixKind, type ReconStudent } from '@/lib/reconFixes';
import { LEDGER_KIND_LABEL, type LedgerLine } from '@/lib/pastelLedger';

const ICON: Record<FixKind, typeof Link2> = {
  linkCode: Link2, linkPupil: UserSearch, addPupil: UserPlus, useName: PenLine, voidInvoice: Ban, recordPayment: HandCoins, raiseInvoice: FilePlus2, reversePayment: RotateCcw, acknowledge: StickyNote,
};

const BAR: Record<string, string> = {
  differs: 'border-l-red-500', missing_in_portal: 'border-l-amber-500', missing_in_pastel: 'border-l-purple-500',
  name_only: 'border-l-blue-500', agree: 'border-l-emerald-500',
};

export interface FixRunners {
  linkCode: (f: Fix) => Promise<void>;
  useName: (f: Fix, name: string) => Promise<void>;
  voidInvoice: (f: Fix, reason: string) => Promise<void>;
  recordPayment: (f: Fix, p: { invoiceId: string; amount: number; date: string; method: PaymentMethod; reference: string; note: string }) => Promise<void>;
  raiseInvoice: (f: Fix, p: { amount: number; label: string; category: FeeCategory; term: TermKey; dueDate: string }) => Promise<void>;
  reversePayment: (f: Fix, reason: string) => Promise<void>;
  linkPupil: (f: Fix, studentId: string) => Promise<void>;
  addPupil: (f: Fix, name: string) => Promise<void>;
  acknowledge: (note: string) => Promise<void>;
}

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n: number) => new Date(Date.now() + n * 864e5).toISOString().slice(0, 10);
const outstanding = (i: Invoice) => Math.round(((Number(i.total) || 0) - (Number(i.paid) || 0) - (Number(i.adjusted) || 0)) * 100) / 100;

export default function PastelFixCard({
  row, fixes, invoices, fileName, run, flag, entries, since, portalFlags, pupils,
}: {
  /** Pupils with no Pastel code, for "Link to a pupil…". */
  pupils?: ReconStudent[];
  /** Portal entries Pastel does not carry, one line each (lib/reconFixes portalFlags). */
  portalFlags?: string[];
  /** A warning to show above the buttons, e.g. "Marked as left in the portal…". */
  flag?: string | null;
  /** Pastel's entries on this account since `since`, from the Detailed Ledger. */
  entries?: LedgerLine[];
  /** The pupil's opening-balance date, YYYY-MM-DD. */
  since?: string;
  row: DiffRow;
  fixes: Fix[];
  /** The pupil's live invoices. */
  invoices: Invoice[];
  fileName: string;
  run: FixRunners;
}) {
  const [open, setOpen] = useState<Fix | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  // Form state, reset whenever a fix is opened.
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today());
  const [method, setMethod] = useState<PaymentMethod>('eft');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState<FeeCategory>('tuition');
  const [term, setTerm] = useState<TermKey>(currentTermKey());
  const [due, setDue] = useState(inDays(14));
  const [pupilQuery, setPupilQuery] = useState('');
  const [pupilId, setPupilId] = useState('');

  // Closest names first; typing narrows to any pupil whose name holds the words.
  const pupilChoices = (() => {
    if (!pupils || open?.kind !== 'linkPupil') return [];
    const q = pupilQuery.trim().toLowerCase();
    const pool = q ? pupils.filter((p) => q.split(/\s+/).every((w) => p.name.toLowerCase().includes(w))) : pupils;
    return pool
      .map((p) => ({ p, l: nameLikeness(row.pastelName, p.name).shared }))
      .sort((a, b) => b.l - a.l || a.p.name.localeCompare(b.p.name))
      .slice(0, 30)
      .map(({ p }) => p);
  })();

  const payable = invoices.filter((i) => i.status !== 'void');
  const byOldest = [...payable].sort((a, b) => String(a.issuedAt).localeCompare(String(b.issuedAt)));

  function choose(f: Fix) {
    setError('');
    setDone('');
    if (f.kind === 'linkCode') { void go(() => run.linkCode(f), 'Code linked.'); return; }
    setOpen(f);
    setName(f.name ?? '');
    setReason(f.kind === 'reversePayment'
      ? `No such receipt in Pastel (${fileName}) — entered in error.`
      : 'Not charged in Pastel — the fees were paid up front.');
    setAmount(f.amount ? String(f.amount) : '');
    setDate(f.date ?? today());
    setMethod('eft');
    setReference(f.reference ?? '');
    setNote(f.kind === 'acknowledge' ? (f.note ?? '') : `Per Pastel ledger ${fileName}${f.reference ? ` (${f.reference})` : ''}`);
    setInvoiceId((byOldest.find((i) => outstanding(i) > 0) ?? byOldest[0])?.id ?? '');
    setLabel(`Fees per Pastel ledger (${fileName.replace(/\.xlsx?$/i, '')})`);
    setCategory('tuition');
    setTerm(currentTermKey());
    setDue(inDays(14));
    setPupilQuery(f.kind === 'linkPupil' ? (row.pastelName.split(/\s+/)[0] ?? '').toLowerCase() : '');
    setPupilId('');
  }

  async function go(fn: () => Promise<void>, msg: string) {
    setBusy(true);
    setError('');
    try {
      await fn();
      setDone(msg);
      setOpen(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function confirm() {
    if (!open) return;
    const amt = Number(amount);
    switch (open.kind) {
      case 'useName': return go(() => run.useName(open, name), 'Name updated.');
      case 'voidInvoice': return go(() => run.voidInvoice(open, reason), 'Invoice voided.');
      case 'reversePayment': return go(() => run.reversePayment(open, reason), 'Payment reversed.');
      case 'linkPupil':
        if (!pupilId) { setError('Choose the pupil.'); return; }
        return go(() => run.linkPupil(open, pupilId), 'Code linked.');
      case 'addPupil': return go(() => run.addPupil(open, name), open.status === 'left' ? 'Added as a former pupil.' : 'Added — Admissions can enrol them from the Pending tab.');
      case 'recordPayment':
        if (!invoiceId) { setError('Choose the invoice the payment is against.'); return; }
        return go(() => run.recordPayment(open, { invoiceId, amount: amt, date, method, reference, note }), 'Payment recorded.');
      case 'raiseInvoice': return go(() => run.raiseInvoice(open, { amount: amt, label, category, term, dueDate: due }), 'Invoice raised (not emailed).');
      case 'acknowledge': return go(() => run.acknowledge(note), 'Noted.');
      default: return undefined;
    }
  }

  const field = glassFieldClass('py-1.5 text-sm');
  const terms = Object.keys(SCHOOL.terms) as TermKey[];

  return (
    <div className={`rounded-r-xl border border-gray-100 dark:border-gray-700/70 border-l-4 ${BAR[row.kind] ?? 'border-l-gray-300'} bg-white dark:bg-gray-800 shadow-sm`}>
      <div className="px-4 py-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 dark:text-white">
            {row.portalName || row.pastelName}
            {row.code && <span className="ml-2 font-mono text-xs text-gray-500">{row.code}</span>}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {row.portalName && row.pastelName && row.portalName.toUpperCase() !== row.pastelName.toUpperCase() && <>Pastel: {row.pastelName} · </>}
            {row.className || row.category || '—'}
          </p>
        </div>
        <div className="flex gap-4 text-right text-sm tabular-nums">
          <div><p className="text-[11px] text-gray-400">Pastel</p><p className="font-semibold">{row.kind === 'missing_in_pastel' ? '—' : fmtBalance(row.pastel)}</p></div>
          <div><p className="text-[11px] text-gray-400">Portal</p><p className="font-semibold">{row.kind === 'missing_in_portal' ? '—' : fmtBalance(row.portal)}</p></div>
          <div><p className="text-[11px] text-gray-400">Difference</p><p className="font-bold text-gray-900 dark:text-white">{Math.abs(row.difference) < 0.5 ? '—' : `${row.difference > 0 ? '+' : ''}${fmtBalance(row.difference)}`}</p></div>
        </div>
      </div>

      {flag && (
        <p className="mx-4 mb-2 rounded-md bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 text-xs font-medium px-3 py-1.5">{flag}</p>
      )}

      {portalFlags && portalFlags.length > 0 && (
        <div className="mx-4 mb-2 rounded-md bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-300 text-xs px-3 py-2">
          <p className="font-semibold flex items-center gap-1.5"><AlertTriangle size={13} /> Entered in the portal, not in Pastel — check with whoever made it</p>
          <ul className="mt-1 space-y-0.5 tabular-nums">
            {portalFlags.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>
      )}

      {entries && (
        <details className="mx-4 mb-2 text-xs">
          <summary className="cursor-pointer text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5">
            <ListTree size={13} /> Pastel entries since {since ? new Date(`${since}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'the opening balance'} ({entries.length})
          </summary>
          {entries.length === 0
            ? <p className="mt-1.5 text-gray-400">None — Pastel's balance is the one it carried then.</p>
            : (
              <table className="mt-1.5 w-full tabular-nums">
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {entries.map((l, i) => (
                    <tr key={`${l.ref}-${i}`}>
                      <td className="py-0.5 pr-3 text-gray-500">{l.date}</td>
                      <td className="py-0.5 pr-3 text-gray-700 dark:text-gray-200">{LEDGER_KIND_LABEL[l.kind]}</td>
                      <td className="py-0.5 pr-3 font-mono text-gray-500">{l.ref}</td>
                      <td className="py-0.5 text-right text-gray-800 dark:text-gray-100">{fmtBalance(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </details>
      )}

      {!open && (
        <div className="px-4 pb-3 flex flex-wrap gap-2">
          {fixes.map((f, i) => {
            const Icon = ICON[f.kind];
            return (
              <button
                key={`${f.kind}-${i}`}
                onClick={() => choose(f)}
                disabled={busy}
                title={f.detail}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${f.recommended
                  ? 'bg-[var(--accent-strong)] text-white border-transparent'
                  : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Icon size={13} />} {f.label}
              </button>
            );
          })}
        </div>
      )}

      {open && (
        <div className="mx-4 mb-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 p-3 space-y-2.5">
          <p className="text-xs text-gray-600 dark:text-gray-300">{open.detail}</p>

          {open.kind === 'useName' && (
            <input value={name} onChange={(e) => setName(e.target.value)} className={field} aria-label="New name" />
          )}

          {open.kind === 'linkPupil' && (
            <div className="space-y-1.5">
              <input value={pupilQuery} onChange={(e) => { setPupilQuery(e.target.value); setPupilId(''); }} className={field} placeholder="Type part of the name" aria-label="Find the pupil" />
              <select value={pupilId} onChange={(e) => setPupilId(e.target.value)} className={field} size={Math.min(6, Math.max(2, pupilChoices.length))} aria-label="Pupil">
                {pupilChoices.map((p) => <option key={p.id} value={p.id}>{p.name}{p.className ? ` — ${p.className}` : p.status ? ` — ${p.status}` : ''}</option>)}
              </select>
              {pupilChoices.length === 0 && <p className="text-[11px] text-gray-400">No pupil without a Pastel code matches. Try fewer letters, or add them to the portal instead.</p>}
            </div>
          )}

          {open.kind === 'addPupil' && (
            <div className="space-y-1.5">
              <label className="text-xs text-gray-500">Name, as the portal should show it<input value={name} onChange={(e) => setName(e.target.value)} className={field} /></label>
              <p className="text-[11px] text-gray-400">Pastel code {open.code} · opening balance {fmtBalance(open.amount ?? 0)} · {open.status === 'left' ? 'a former pupil (no entries this year)' : 'pending — Admissions enrols them with a class'}.</p>
            </div>
          )}

          {open.kind === 'reversePayment' && (
            <input value={reason} onChange={(e) => setReason(e.target.value)} className={field} aria-label="Reason for reversing" placeholder="Why it is being reversed" />
          )}

          {open.kind === 'voidInvoice' && (
            <input value={reason} onChange={(e) => setReason(e.target.value)} className={field} aria-label="Reason for voiding" placeholder="Why it is being voided" />
          )}

          {open.kind === 'recordPayment' && (
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="text-xs text-gray-500">Amount (P)<input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={field} /></label>
              <label className="text-xs text-gray-500">Date received<input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={field} /></label>
              <label className="text-xs text-gray-500">Method
                <select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)} className={field}>
                  {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>)}
                </select>
              </label>
              <label className="text-xs text-gray-500">Reference<input value={reference} onChange={(e) => setReference(e.target.value)} className={field} placeholder="Bank or Pastel receipt reference" /></label>
              <label className="text-xs text-gray-500 sm:col-span-2">Against invoice
                <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} className={field}>
                  {byOldest.length === 0 && <option value="">No invoice on this account</option>}
                  {byOldest.map((i) => <option key={i.id} value={i.id}>{i.invoiceNo} · {fmtBalance(Number(i.total) || 0)} · {fmtBalance(outstanding(i))} outstanding</option>)}
                </select>
              </label>
              <label className="text-xs text-gray-500 sm:col-span-2">Note<input value={note} onChange={(e) => setNote(e.target.value)} className={field} /></label>
            </div>
          )}

          {open.kind === 'raiseInvoice' && (
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="text-xs text-gray-500 sm:col-span-2">What it is for<input value={label} onChange={(e) => setLabel(e.target.value)} className={field} /></label>
              <label className="text-xs text-gray-500">Amount (P)<input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={field} /></label>
              <label className="text-xs text-gray-500">Category
                <select value={category} onChange={(e) => setCategory(e.target.value as FeeCategory)} className={field}>
                  {(Object.keys(FEE_CATEGORY_LABELS) as FeeCategory[]).map((c) => <option key={c} value={c}>{FEE_CATEGORY_LABELS[c]}</option>)}
                </select>
              </label>
              <label className="text-xs text-gray-500">Term
                <select value={term} onChange={(e) => setTerm(e.target.value as TermKey)} className={field}>
                  {terms.map((t) => <option key={t} value={t}>{termLabel(t)}</option>)}
                </select>
              </label>
              <label className="text-xs text-gray-500">Due<input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={field} /></label>
              <p className="text-[11px] text-gray-400 sm:col-span-2">The invoice is not emailed. Send it from Statements when you are ready.</p>
            </div>
          )}

          {open.kind === 'acknowledge' && (
            <input value={note} onChange={(e) => setNote(e.target.value)} className={field} placeholder="Why the portal is right, e.g. “Pastel not yet updated for the September payment”" aria-label="Note" />
          )}

          <div className="flex gap-2">
            <button className={btnPrimary} disabled={busy} onClick={confirm}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Confirm
            </button>
            <button className={btnGhost} disabled={busy} onClick={() => setOpen(null)}>Cancel</button>
          </div>
        </div>
      )}

      {(error || done) && (
        <p className={`px-4 pb-3 text-xs ${error ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-300'}`}>{error || done}</p>
      )}
    </div>
  );
}
